import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import {
  LayoutDashboard,
  FileEdit,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  ExternalLink,
  UserCheck,
} from 'lucide-react';

const T = {
  bg: '#faf9f6',
  sidebar: '#ffffff',
  sidebarBorder: '#e8e5e0',
  vert: '#1e4a1a',
  or: '#c4a76c',
  card: '#ffffff',
  textPrimary: '#1a1a1a',
  textSecondary: '#6b7280',
  textMuted: '#9ca3af',
  border: '#e8e5e0',
  hoverBg: '#f5f3ef',
  activeBg: 'rgba(30,74,26,0.06)',
};

const navigation = [
  { name: 'Vue d\'ensemble', href: '/admin', icon: LayoutDashboard },
  { name: 'Contenu & Galerie', href: '/admin/content', icon: FileEdit },
  { name: 'Prospects & Brochures', href: '/admin/prospects', icon: UserCheck },
  { name: 'Audience & Visites', href: '/admin/audience', icon: BarChart3 },
  { name: 'Paramètres', href: '/admin/settings', icon: Settings },
];

const AdminLayout = ({ children }) => {
  const { user, logout } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/ogd-access-gate');
  };

  const isActive = (href) => {
    if (href === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: T.bg }} data-testid="admin-layout">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/20 z-40 lg:hidden backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-60 transform transition-transform duration-300 ease-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ backgroundColor: T.sidebar, borderRight: `1px solid ${T.sidebarBorder}` }}
        data-testid="admin-sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-20 px-6">
            <Link to="/admin" className="flex items-center gap-3">
              <img
                src="/assets/logos/logo-dore.png"
                alt="One Green Dev"
                className="h-[52px] w-auto object-contain"
                data-testid="admin-sidebar-logo"
              />
            </Link>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden" style={{ color: T.textMuted }}>
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="mx-5 h-px" style={{ backgroundColor: T.border }} />

          {/* Nav */}
          <nav className="flex-1 px-3 py-6 space-y-1">
            {navigation.map((item) => {
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.href.split('/').pop() || 'overview'}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-[13px] font-medium transition-all duration-200"
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    color: active ? T.vert : T.textSecondary,
                    backgroundColor: active ? T.activeBg : 'transparent',
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = T.hoverBg; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <item.icon className="w-[18px] h-[18px]" style={{ color: active ? T.vert : T.textMuted }} />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="px-3 pb-6">
            <div className="mx-3 mb-4 h-px" style={{ backgroundColor: T.border }} />
            <a
              href="/" target="_blank" rel="noopener noreferrer"
              data-testid="view-site-link"
              className="flex items-center gap-2.5 px-4 py-2.5 text-[12px] rounded-xl transition-colors"
              style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}
              onMouseEnter={(e) => { e.currentTarget.style.color = T.vert; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = T.textMuted; }}
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Voir le site
            </a>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="flex items-center gap-2.5 px-4 py-2.5 text-[12px] w-full rounded-xl transition-colors"
              style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = T.textMuted; }}
            >
              <LogOut className="w-3.5 h-3.5" />
              Deconnexion
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-60">
        {/* Top bar */}
        <header className="sticky top-0 z-30" style={{ backgroundColor: 'rgba(250,249,246,0.85)', backdropFilter: 'blur(12px)', borderBottom: `1px solid ${T.border}` }}>
          <div className="flex items-center justify-between h-16 px-5 lg:px-8">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden" style={{ color: T.textSecondary }}>
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 ml-auto">
              <div className="text-right">
                <p className="text-[13px] font-medium" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textPrimary }}>
                  {user?.name}
                </p>
                <p className="text-[9px] uppercase tracking-[0.15em]" style={{ fontFamily: "'Montserrat', sans-serif", color: T.or }}>
                  {user?.role === 'admin' ? 'Administrateur' : 'Editeur'}
                </p>
              </div>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: T.vert }}>
                <span className="text-xs font-bold text-white">{user?.name?.charAt(0)?.toUpperCase()}</span>
              </div>
            </div>
          </div>
        </header>

        <main className="p-5 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
